import { useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { SITE_URL } from '../lib/constants';

export const SEO = ({ path, title, description, keywords, image, jsonLd }) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const ogImage = image || `${SITE_URL}/og-default.jpg`;

    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [, key, val] = selector.match(/^meta\[(name|property)="([^"]+)"\]$/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);

    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:site_name"]', 'content', 'Padelarena Wädenswil');

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Inject page-specific JSON-LD script(s)
    const scripts = [];
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.filter(Boolean).forEach((block) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.seoPage = path;
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); });
    };
  }, [path, title, description, keywords, image, jsonLd]);

  return null;
};

// Reusable schema fragments
export const ORGANIZATION_LD = {
  '@type': 'SportsClub',
  '@id': `${SITE_URL}/#organization`,
  name: 'Padelarena Wädenswil',
  url: SITE_URL,
  telephone: '+41 43 833 0834',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Schönenbergstrasse 25',
    addressLocality: 'Wädenswil',
    addressRegion: 'ZH',
    postalCode: '8820',
    addressCountry: 'CH'
  },
  areaServed: ['Wädenswil', 'Zürich', 'Zürichsee', 'Horgen', 'Pfäffikon', 'Richterswil', 'Thalwil']
};

// FAQ block component (also used to render schema)
export const FaqSection = ({ faqs, title }) => (
  <div>
    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{title}</h2>
    <div className="space-y-4">
      {faqs.map((f, i) => (
        <details key={i} className="group bg-slate-50 border border-slate-100 rounded-2xl p-5 cursor-pointer">
          <summary className="font-bold text-slate-900 list-none flex justify-between items-start gap-4">
            <span>{f.q}</span>
            <ChevronDown className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-180" />
          </summary>
          <p className="text-slate-700 leading-relaxed mt-3">{f.a}</p>
        </details>
      ))}
    </div>
  </div>
);

export const buildFaqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a }
  }))
});
